import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Upload, CheckCircle2, ShieldAlert, IdCard, FileText, HomeIcon, DollarSign,
  ArrowRight, Sparkles, Car, TrendingUp, ShieldCheck, ArrowLeft,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PLATFORMS = ["Uber", "Lyft", "Uber Eats", "DoorDash", "Instacart", "Amazon Flex"];

export default function DriverRentals() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [step, setStep] = useState(0);
  const [chosen, setChosen] = useState(null);
  const [weeks, setWeeks] = useState(4);
  const [estimate, setEstimate] = useState(null);
  const [existing, setExisting] = useState([]);

  useEffect(() => {
    api.get("/vehicles", { params: { available_for: "rental" } })
      .then((r) => setVehicles(r.data.filter((v) => v.rideshare_ready)))
      .catch(() => setVehicles([]));
    if (user) {
      api.get("/driver-rentals/me").then((r) => setExisting(r.data || []));
    }
  }, [user]);

  useEffect(() => {
    if (!chosen) return;
    api.post("/driver-rentals/estimate", { vehicle_id: chosen.id, weeks })
      .then((r) => setEstimate(r.data));
  }, [chosen, weeks]);

  const startApplication = (v) => {
    if (!user) return navigate("/login", { state: { from: "/driver-rentals" } });
    setChosen(v);
    setStep(1);
  };

  const activeRental = existing.find((r) => r.status !== "completed" && r.status !== "cancelled");

  return (
    <section data-testid="driver-rentals-page" className="mx-auto max-w-7xl px-5 sm:px-8 py-12">
      {step === 0 && (
        <>
          <div className="mb-12 max-w-2xl">
            <div className="label-caps text-cream mb-3">Rideshare & Delivery Rentals</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight">
              Drive for Uber, Lyft, DoorDash — <span className="text-cream italic">on our fleet</span>.
            </h1>
            <p className="text-white/70 mt-5 leading-relaxed">
              Weekly rentals cleared for every major rideshare and delivery platform. No credit checks
              in most cases, refundable deposit, insurance included. Start earning today.
            </p>
          </div>

          {activeRental && (
            <div className="glass rounded-3xl p-6 mb-10 flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="label-caps text-cream mb-1">You have an active rental</div>
                <div className="font-serif text-xl">{activeRental.vehicle_name}</div>
                <div className="text-sm text-white/60">Status: {activeRental.status}</div>
              </div>
              <Link to="/driver-rentals/dashboard"
                data-testid="dr-goto-dashboard"
                className="btn-cream px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2">
                Go to my dashboard <ArrowRight size={14} />
              </Link>
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-4 mb-12">
            {[
              { icon: DollarSign, title: "Weekly billing", body: "Flat weekly rate. AutoPay optional. Pause anytime." },
              { icon: ShieldCheck, title: "Full insurance", body: "Commercial coverage on the vehicle. Focus on driving." },
              { icon: TrendingUp, title: "All platforms cleared", body: "Uber, Lyft, Uber Eats, DoorDash & more." },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="glass rounded-2xl p-6">
                <Icon size={22} className="text-cream mb-3" />
                <div className="font-serif text-xl mb-1.5">{title}</div>
                <p className="text-sm text-white/60 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>

          <div className="label-caps text-cream mb-4">Pick your vehicle</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vehicles.length === 0 && (
              <div className="glass rounded-2xl p-10 text-white/50 col-span-full text-center">
                Rideshare-ready vehicles are being prepped. Check back shortly.
              </div>
            )}
            {vehicles.map((v) => (
              <div key={v.id} data-testid={`dr-vehicle-${v.id}`}
                className="glass rounded-2xl overflow-hidden group hover:border-cream/30 transition-colors">
                <img src={v.image_url} alt={v.name} className="w-full aspect-[16/10] object-cover"
                  onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=1400&q=80"; }} />
                <div className="p-5">
                  <div className="label-caps text-white/40 mb-1">{v.category.replace(/-/g, " ")}</div>
                  <h3 className="font-serif text-xl mb-2">{v.name}</h3>
                  <div className="text-2xl font-serif text-cream mb-1">
                    ${(v.daily_rate * 5).toFixed(0)}<span className="text-sm text-white/50">/week</span>
                  </div>
                  <p className="text-xs text-white/50 mb-4">Refundable deposit ${(v.daily_rate * 5).toFixed(0)}</p>
                  <button data-testid={`dr-apply-${v.id}`} onClick={() => startApplication(v)}
                    className="btn-cream w-full py-2.5 rounded-full text-sm inline-flex items-center justify-center gap-2">
                    Apply for this vehicle <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {step >= 1 && chosen && (
        <ApplicationWizard
          vehicle={chosen} weeks={weeks} setWeeks={setWeeks}
          estimate={estimate} step={step} setStep={setStep}
          onCancel={() => { setStep(0); setChosen(null); }}
          onComplete={() => navigate("/driver-rentals/dashboard")}
        />
      )}
    </section>
  );
}

/* ============================================================ */

function ApplicationWizard({ vehicle, weeks, setWeeks, estimate, step, setStep, onCancel, onComplete }) {
  const { user, refresh } = useAuth();
  const [docs, setDocs] = useState({ license: null, insurance: null, proof_of_address: null });
  const [app, setApp] = useState({
    full_legal_name: user?.name || "", date_of_birth: "", address: "",
    license_number: "", license_expiration: "",
    insurance_carrier: "", insurance_policy_number: "",
    rideshare_platforms: [], background_check_consent: false,
  });
  const [rental, setRental] = useState(null);
  const [signature, setSignature] = useState("");
  const [busy, setBusy] = useState(false);

  const togglePlatform = (p) => {
    setApp((a) => ({ ...a,
      rideshare_platforms: a.rideshare_platforms.includes(p)
        ? a.rideshare_platforms.filter((x) => x !== p)
        : [...a.rideshare_platforms, p] }));
  };

  const uploadDoc = async (kind, file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/uploads/document/${kind}`, fd);
      setDocs((d) => ({ ...d, [kind]: data.file_id }));
      toast.success(`${kind.replace("_", " ")} uploaded`);
      if (kind === "license") refresh();
    } catch (e) { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };

  const canProceedDocs = docs.license && docs.insurance && docs.proof_of_address;
  const canProceedApp = app.full_legal_name && app.date_of_birth && app.address &&
                        app.license_number && app.license_expiration &&
                        app.background_check_consent;

  const submitApplication = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/driver-rentals", {
        vehicle_id: vehicle.id, weeks, ...docs,
        license_file_id: docs.license,
        insurance_file_id: docs.insurance,
        proof_of_address_file_id: docs.proof_of_address,
        application: app,
      });
      setRental(data);
      setStep(3);
    } catch (e) { toast.error(e?.response?.data?.detail || "Application failed"); }
    finally { setBusy(false); }
  };

  const signAgreement = async () => {
    if (!signature.trim()) return toast.error("Please type your legal name to sign");
    setBusy(true);
    try {
      await api.post(`/driver-rentals/${rental.id}/sign`, { signature_name: signature });
      toast.success("Agreement signed");
      setStep(4);
    } catch (e) { toast.error("Sign failed"); }
    finally { setBusy(false); }
  };

  const payDeposit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/driver-rentals/${rental.id}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) { toast.error("Payment setup failed"); setBusy(false); }
  };

  const stepLabels = ["Vehicle", "Documents", "Application", "Sign", "Deposit"];

  return (
    <div className="max-w-4xl mx-auto">
      <button onClick={onCancel} className="text-sm text-white/60 hover:text-white inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> Back to vehicles
      </button>

      <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
        {stepLabels.map((label, i) => (
          <div key={label} className="flex items-center gap-2 flex-shrink-0">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${
              step === i ? "bg-cream text-ink-900" :
              step > i ? "bg-cream/40 text-ink-900" : "bg-ink-700 text-white/50"
            }`}>{i + 1}</div>
            <span className={`label-caps text-xs ${step === i ? "text-cream" : "text-white/40"}`}>{label}</span>
            {i < stepLabels.length - 1 && <div className="w-6 h-px bg-white/10" />}
          </div>
        ))}
      </div>

      <div className="glass rounded-3xl p-6 sm:p-8">
        <div className="mb-6 flex items-center gap-4">
          <img src={vehicle.image_url} alt="" className="w-24 h-16 rounded-lg object-cover" />
          <div>
            <div className="label-caps text-white/50">Applying for</div>
            <div className="font-serif text-2xl">{vehicle.name}</div>
          </div>
        </div>

        {step === 1 && (
          <>
            <h3 className="font-serif text-2xl mb-2">Upload three documents</h3>
            <p className="text-sm text-white/60 mb-6">
              Clear photos of the front only. JPG, PNG, or PDF up to 8MB. We use bank-grade encryption.
            </p>
            <div className="space-y-3 mb-6">
              <DocRow icon={IdCard} label="Driver's license" kind="license" file_id={docs.license} onUpload={uploadDoc} testid="dr-doc-license" />
              <DocRow icon={FileText} label="Insurance card" kind="insurance" file_id={docs.insurance} onUpload={uploadDoc} testid="dr-doc-insurance" />
              <DocRow icon={HomeIcon} label="Proof of address (utility bill or lease)" kind="proof_of_address" file_id={docs.proof_of_address} onUpload={uploadDoc} testid="dr-doc-address" />
            </div>
            <div className="mb-6">
              <div className="label-caps text-white/60 mb-2">Weeks to commit</div>
              <div className="flex gap-2 flex-wrap">
                {[1, 2, 4, 8, 12, 26, 52].map((w) => (
                  <button key={w} onClick={() => setWeeks(w)}
                    data-testid={`dr-weeks-${w}`}
                    className={`px-4 py-2 rounded-full text-xs label-caps ${
                      weeks === w ? "bg-cream text-ink-900" : "border border-white/10 text-white/60 hover:text-white"
                    }`}>{w} {w === 1 ? "week" : "weeks"}</button>
                ))}
              </div>
            </div>
            <button data-testid="dr-continue-1" disabled={!canProceedDocs} onClick={() => setStep(2)}
              className="btn-cream w-full py-3 rounded-full text-sm disabled:opacity-40 inline-flex items-center justify-center gap-2">
              Continue <ArrowRight size={14} />
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <h3 className="font-serif text-2xl mb-2">Application</h3>
            <p className="text-sm text-white/60 mb-6">All fields required. Match exactly what is on your license.</p>
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              <Field label="Full legal name" value={app.full_legal_name} onChange={(v) => setApp({ ...app, full_legal_name: v })} testid="dr-app-name" />
              <Field label="Date of birth" type="date" value={app.date_of_birth} onChange={(v) => setApp({ ...app, date_of_birth: v })} testid="dr-app-dob" />
              <Field label="Address" value={app.address} onChange={(v) => setApp({ ...app, address: v })} testid="dr-app-address" full />
              <Field label="License number" value={app.license_number} onChange={(v) => setApp({ ...app, license_number: v })} testid="dr-app-lic" />
              <Field label="License expiration" type="date" value={app.license_expiration} onChange={(v) => setApp({ ...app, license_expiration: v })} testid="dr-app-lic-exp" />
              <Field label="Insurance carrier" value={app.insurance_carrier} onChange={(v) => setApp({ ...app, insurance_carrier: v })} testid="dr-app-ins-carrier" />
              <Field label="Insurance policy #" value={app.insurance_policy_number} onChange={(v) => setApp({ ...app, insurance_policy_number: v })} testid="dr-app-ins-policy" />
            </div>
            <div className="mb-5">
              <div className="label-caps text-white/60 mb-2">Platforms you'll drive on</div>
              <div className="flex flex-wrap gap-2">
                {PLATFORMS.map((p) => (
                  <button key={p} type="button" onClick={() => togglePlatform(p)}
                    data-testid={`dr-platform-${p.toLowerCase().replace(/ /g, "-")}`}
                    className={`px-3 py-1.5 rounded-full text-xs ${
                      app.rideshare_platforms.includes(p) ? "bg-cream text-ink-900" : "border border-white/10 text-white/60"
                    }`}>{p}</button>
                ))}
              </div>
            </div>
            <label className="flex items-start gap-3 cursor-pointer mb-6">
              <input type="checkbox" checked={app.background_check_consent}
                onChange={(e) => setApp({ ...app, background_check_consent: e.target.checked })}
                data-testid="dr-app-consent"
                className="mt-1 accent-cream w-4 h-4" />
              <span className="text-sm text-white/70">
                I authorize Plenty Rydez to run a background check and DMV record check
                as part of this rental application.
              </span>
            </label>
            <div className="flex gap-2">
              <button onClick={() => setStep(1)}
                className="px-5 py-3 rounded-full text-sm border border-white/10 hover:border-white/30">Back</button>
              <button data-testid="dr-submit-app" disabled={!canProceedApp || busy} onClick={submitApplication}
                className="btn-cream flex-1 py-3 rounded-full text-sm disabled:opacity-40">
                {busy ? "Submitting…" : "Submit application"}
              </button>
            </div>
          </>
        )}

        {step === 3 && rental && (
          <>
            <h3 className="font-serif text-2xl mb-2">Rental agreement</h3>
            <div className="max-h-64 overflow-y-auto text-sm text-white/70 bg-ink-800 rounded-xl p-5 mb-4 leading-relaxed">
              <p className="mb-2"><strong className="text-cream">Between:</strong> Plenty Rydez LLC ("Owner") and {app.full_legal_name} ("Renter").</p>
              <p className="mb-2"><strong className="text-cream">Vehicle:</strong> {vehicle.name}.</p>
              <p className="mb-2"><strong className="text-cream">Term:</strong> {rental.weeks_committed} week(s), starting on payment of the deposit and first week.</p>
              <p className="mb-2"><strong className="text-cream">Weekly rate:</strong> ${rental.weekly_rate.toFixed(2)}.</p>
              <p className="mb-2"><strong className="text-cream">Deposit:</strong> ${rental.deposit.toFixed(2)}, fully refundable on undamaged return.</p>
              <p className="mb-2">Renter agrees to hold a valid, in-date US or Canadian driver's license for the duration of the term. Insurance is included; Renter is responsible for the deductible on any claim caused by their operation of the vehicle.</p>
              <p className="mb-2">Renter agrees the vehicle may only be used for lawful ride-share, delivery, or personal use, and may not be operated by any other person without prior written consent from Plenty Rydez.</p>
              <p>By typing my name below I acknowledge I have read and agree to this Rental Agreement.</p>
            </div>
            <label className="block mb-4">
              <div className="label-caps text-white/60 mb-2">Type your full legal name to sign</div>
              <input value={signature} onChange={(e) => setSignature(e.target.value)}
                data-testid="dr-signature"
                placeholder={app.full_legal_name}
                className="w-full bg-ink-800 border border-white/10 rounded-xl px-4 py-3 font-serif italic text-xl focus:border-cream outline-none" />
            </label>
            <button data-testid="dr-sign" onClick={signAgreement} disabled={busy || !signature.trim()}
              className="btn-cream w-full py-3 rounded-full text-sm disabled:opacity-40">
              {busy ? "Signing…" : "Sign & continue to deposit"}
            </button>
          </>
        )}

        {step === 4 && rental && (
          <>
            <h3 className="font-serif text-2xl mb-2">Deposit + first week</h3>
            <p className="text-sm text-white/60 mb-6">
              Pay your refundable deposit and first week to lock in your rental. Weekly renewals afterwards.
            </p>
            <div className="glass rounded-2xl p-5 mb-6 space-y-2">
              <Row k="Refundable deposit" v={`$${rental.deposit.toFixed(2)}`} />
              <Row k="First week" v={`$${rental.weekly_rate.toFixed(2)}`} />
              <div className="border-t border-white/10 my-2" />
              <Row k={<strong>Due today</strong>}
                   v={<strong className="text-cream text-lg">${(rental.deposit + rental.weekly_rate).toFixed(2)}</strong>} />
            </div>
            <button data-testid="dr-pay" onClick={payDeposit} disabled={busy}
              className="btn-cream w-full py-3 rounded-full text-sm disabled:opacity-40 inline-flex items-center justify-center gap-2">
              {busy ? "Preparing…" : <>Pay & activate rental <ArrowRight size={14} /></>}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const DocRow = ({ icon: Icon, label, kind, file_id, onUpload, testid }) => {
  const ref = useRef(null);
  return (
    <div className="flex items-center justify-between gap-3 p-4 rounded-xl bg-ink-800 border border-white/5">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <Icon size={16} className="text-cream flex-shrink-0" />
        <div className="min-w-0">
          <div className="text-sm truncate">{label}</div>
          <div className="text-xs text-white/50">
            {file_id ? <span className="text-cream flex items-center gap-1"><CheckCircle2 size={10} /> On file</span> : "Not uploaded"}
          </div>
        </div>
      </div>
      <input ref={ref} type="file" accept="image/*,application/pdf" className="hidden"
        onChange={(e) => onUpload(kind, e.target.files?.[0])} data-testid={testid} />
      <button onClick={() => ref.current?.click()}
        className="btn-outline-platinum px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1 hover:text-white">
        <Upload size={12} /> {file_id ? "Replace" : "Upload"}
      </button>
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text", testid, full }) => (
  <label className={`block ${full ? "sm:col-span-2" : ""}`}>
    <div className="label-caps text-white/60 mb-1">{label}</div>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full bg-ink-800 border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-cream outline-none" />
  </label>
);

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-white/60">{k}</span>
    <span className="text-white">{v}</span>
  </div>
);
